/**
 * A history entry's change to a document's file (a document's or a spreadsheet's of Revisa's own), drawn: the version
 * before it and the one it made, side by side and read-only (office/VersionCompare.tsx, in the documents' chunk), the
 * one it made restored or saved as a new document. With `?copy=` (a copy kept on this device, office/sync/recovery.ts:
 * the editor's Versions, the recovery notice), that copy beside the version it made, restored or saved as a recovery
 * copy.
 */

import { useEffect, useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { ItemDetail, PageChangeView, SpaceOut, Version, VersionList } from "../../api/types.gen.ts";
import { locale, t } from "../../i18n/index.ts";
import { showDark } from "../../lib/documents.ts";
import { formatInstant } from "../../lib/format.ts";
import { ignore } from "../../lib/ignore.ts";
import { spaceApi, spaceUrl } from "../../lib/paths.ts";
import { asApiError } from "../../lib/resource.ts";
import { canEdit } from "../../lib/roles.ts";
import { location, navigate } from "../../lib/router.ts";
import { toast } from "../../lib/toast.ts";
import { type DocumentsModule, documentsChunk } from "../../office/documentsChunk.ts";
import { type Copy, recoveryStore } from "../../office/sync/recovery.ts";
import { Button } from "../../ui/Button.tsx";
import { ConfirmButton } from "../../ui/Dialog.tsx";
import { NewDocumentIcon, NextIcon, PreviousIcon, RestoreVersionIcon } from "../../ui/icons.tsx";
import { Spinner } from "../../ui/Spinner.tsx";
import { iconButtonClass } from "../../ui/styles.ts";

/** A document's or spreadsheet's file, of Revisa's own. */
const NATIVE = new Set(["application/vnd.revisa.document+json", "application/vnd.revisa.spreadsheet+json"]);

interface FileOf {
  sha256: string;
  mime: string;
}

const fileOf = (o: unknown): FileOf | null => (o as { file?: FileOf } | null | undefined)?.file ?? null;

/** A change to a document's file: the item, its title, its file before (none: made by it) and after. */
interface DocChange {
  item: string;
  title: string;
  before: string | null;
  after: string;
}

function docChanges(changes: PageChangeView["data"]): DocChange[] {
  const out: DocChange[] = [];
  for (const c of changes) {
    const after = fileOf(c.after);
    if (c.kind !== "item" || !after || !NATIVE.has(after.mime)) {
      continue;
    }
    const before = fileOf(c.before)?.sha256 ?? null;
    if (before !== after.sha256) {
      const title = (c.after as { title?: string }).title ?? "";
      out.push({ item: c.id, title, before, after: after.sha256 });
    }
  }
  return out;
}

/** How many of a document's latest versions its neighbours are found among. */
const NEIGHBOURS = 200;

let chunk: DocumentsModule | null = null;

function useChunk(): DocumentsModule | null {
  const [mod, setMod] = useState<DocumentsModule | null>(chunk);
  useEffect(() => {
    if (!chunk) {
      documentsChunk.load().then((m) => {
        chunk = m;
        setMod(m);
      }, ignore);
    }
  }, []);
  return mod;
}

/** The copy `?copy=` names, kept here for `item` (null: none, or not this document's). */
function useCopy(item: string): Copy | null {
  const id = location.value.searchParams.get("copy");
  const [copy, setCopy] = useState<Copy | null>(null);
  useEffect(() => {
    if (!id) {
      setCopy(null);
      return;
    }
    recoveryStore()
      .of(item)
      .then((cs) => setCopy(cs.find((c) => c.id === id) ?? null))
      .catch(() => setCopy(null));
  }, [id, item]);
  return copy;
}

/** The document's versions just older and newer than this entry's (none: there's none, or it isn't one of its latest). */
function useNeighbours(space: SpaceOut, item: string, commit: string) {
  const [around, setAround] = useState<{ older: Version | null; newer: Version | null } | null>(null);
  useEffect(() => {
    let gone = false;
    api
      .get<VersionList>(spaceApi(space, `/items/${item}/versions`), { limit: NEIGHBOURS })
      .then((r) => {
        const kept = r.versions.filter((v) => v.available);
        const at = kept.findIndex((v) => v.commit === commit);
        if (!gone) {
          setAround(at < 0 ? null : { older: kept[at + 1] ?? null, newer: kept[at - 1] ?? null });
        }
      })
      .catch(() => !gone && setAround(null));
    return () => {
      gone = true;
    };
  }, [space, item, commit]);
  return around;
}

function OneChange({
  space,
  commit,
  change,
  mod,
}: {
  space: SpaceOut;
  commit: string;
  change: DocChange;
  mod: DocumentsModule | null;
}) {
  const { item, before, after } = change;
  const around = useNeighbours(space, item, commit);
  // As the editor's version bar has them (VersionPreview.tsx): icons, their names their titles.
  const step = (v: Version | null | undefined, side: "older" | "newer") => {
    const label = t(side === "older" ? "office.versions.older" : "office.versions.newer");
    return (
      <button
        type="button"
        class={iconButtonClass("sm", "secondary")}
        disabled={!v}
        onClick={() => v && navigate(spaceUrl(space, `/history/${v.commit}`))}
        title={label}
        aria-label={label}
        data-action={side === "older" ? "versionOlder" : "versionNewer"}
      >
        {side === "older" ? <PreviousIcon size={16} /> : <NextIcon size={16} />}
      </button>
    );
  };
  const copy = useCopy(item);
  const [busy, setBusy] = useState(false);
  const run = async (what: () => Promise<void>) => {
    setBusy(true);
    try {
      await what();
    } catch (e) {
      toast(asApiError(e).message, "error");
    } finally {
      setBusy(false);
    }
  };
  const content = copy ? (JSON.parse(copy.content) as never) : null;
  const restore = () =>
    run(async () => {
      if (copy) {
        await api.post(spaceApi(space, `/items/${item}/restore`), { content });
      } else {
        await api.post(spaceApi(space, `/items/${item}/versions/${after}/restore`));
      }
      toast(t("office.versions.restored"));
      navigate(spaceUrl(space, `/items/${item}`));
    });
  const saveCopy = () =>
    run(async () => {
      const body = copy ? { content, recovered: copy.lost === true } : { version: after };
      const made = await api.post<ItemDetail>(spaceApi(space, `/items/${item}/copies`), body);
      toast(t("office.recovery.saved", { title: made.title ?? "" }));
      navigate(spaceUrl(space, `/items/${made.id}`));
    });
  const editable = canEdit(space);
  const left = copy
    ? {
        label: t("history.compare.copy", { when: formatInstant(new Date(copy.at).toISOString()) }),
        content: content ?? undefined,
      }
    : before
      ? { label: t("history.before"), sha: before }
      : null;
  return (
    <section class="mb-6 flex flex-col gap-3" data-document-change={item}>
      <div class="flex flex-wrap items-center gap-2">
        <h2 class="mr-auto text-sm font-semibold text-zinc-900">{change.title}</h2>
        {around ? (
          <nav class="mr-2 inline-flex gap-1" aria-label={t("history.compare.steps")}>
            {step(around.older, "older")}
            {step(around.newer, "newer")}
          </nav>
        ) : null}
        {editable ? (
          <Button
            size="sm"
            busy={busy}
            onClick={() => saveCopy().catch(ignore)}
            title={t("office.versions.saveCopy")}
            data-action="versionCopy"
          >
            <NewDocumentIcon size={16} />
            {t("office.versions.saveShort")}
          </Button>
        ) : null}
        {editable ? (
          <ConfirmButton
            label={
              <>
                <RestoreVersionIcon size={16} />
                {t("office.versions.restoreShort")}
              </>
            }
            title={t("office.versions.restoreTitle")}
            message={t("office.versions.restoreMessage")}
            confirmLabel={t("office.versions.restoreConfirm")}
            onConfirm={() => {
              restore().catch(ignore);
            }}
            busy={busy}
            size="sm"
          />
        ) : null}
      </div>
      {mod ? (
        <mod.VersionCompare
          space={space}
          item={item}
          left={left}
          right={{ label: copy ? t("history.compare.this") : t("history.after"), sha: after }}
          locale={locale.value}
          dark={showDark()}
        />
      ) : (
        <Spinner />
      )}
    </section>
  );
}

/** The changes to documents' files among a history entry's (its first page's), each drawn before and after. */
export function DocumentChanges({ space, commit, first }: { space: SpaceOut; commit: string; first: PageChangeView }) {
  const changes = docChanges(first.data);
  const mod = useChunk();
  if (changes.length === 0) {
    return null;
  }
  return (
    <>
      {changes.map((c) => (
        <OneChange key={c.item} space={space} commit={commit} change={c} mod={mod} />
      ))}
    </>
  );
}
